var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { Component } from 'react';
import { Animated, View, TouchableWithoutFeedback, StyleSheet } from 'react-native';
import { DRNUtil } from '@traveloka/district-util';
import PropTypes from 'prop-types';
import VARIANT from './constant/variant';
import SIZE from './constant/size';
import R from '../../../core-res/R';
import DRNTouchableWrapper from '../TouchableButtonWrapper';
import DRNText from '../../../standard/Text/DRNText';
import DRNLoadingIndicator from '../../../standard/LoadingIndicator';
import { getButtonTextStyle } from './util/BaseButtonStyles';
import withThemedStyles from './util/withThemedStyles';
import { withSmartKey } from '../../../smartkey';
let warningMsg = '';
if (process.env.NODE_ENV === 'development') {
    warningMsg =
        'A <Button /> should be provided with either a "text" or a "label" property. Otherwise, it would confuse screen reader users about what it actually does when it has the current focus. If you are using an Icon Button, make sure to properly give a "label" property to the button.';
}
class BaseButton extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            pressed: false,
            focused: false,
        };
        this.getAccessibilityLabel = () => {
            const { accessibilityLabel, text, label } = this.props;
            if (accessibilityLabel != null) {
                return accessibilityLabel;
            }
            return text !== null ? text : label;
        };
        this.handleFocus = () => {
            if (DRNUtil.isWeb()) {
                if (!this.state.focused) {
                    this.setState({
                        focused: true,
                    });
                }
            }
        };
        this.handleBlur = () => {
            if (DRNUtil.isWeb()) {
                if (this.state.focused) {
                    this.setState({
                        focused: false,
                    });
                }
            }
        };
        this.handlePressIn = (...args) => {
            if (DRNUtil.isiOS()) {
                this.setState({ pressed: true });
            }
            if (DRNUtil.isWeb()) {
                if (this.state.focused) {
                    this.setState({
                        focused: false,
                    });
                }
            }
            this.props.onPressIn && this.props.onPressIn(...args);
        };
        this.handlePressOut = (...args) => {
            if (DRNUtil.isiOS()) {
                this.setState({ pressed: false });
            }
            if (DRNUtil.isWeb()) {
                const eventKey = args[0].key;
                if (!this.state.focused && (eventKey === 'Enter' || eventKey === 'Spacebar' || eventKey === ' ')) {
                    this.setState({
                        focused: true,
                    });
                }
            }
            this.props.onPressOut && this.props.onPressOut(...args);
        };
    }
    warnDev(...args) {
        if (process.env.NODE_ENV === 'development') {
            console.warn(...args);
        }
    }
    componentDidMount() {
        const { text, label } = this.props;
        if (!text && !label) {
            this.warnDev(warningMsg);
        }
    }
    componentDidUpdate(prevProps) {
        const { text, label } = this.props;
        if (!text && !label && prevProps.text !== text && prevProps.label !== label) {
            this.warnDev(warningMsg);
        }
    }
    getContainerStyle() {
        const { buttonStyle, disabled, themedContainerStyle } = this.props;
        const btnStyle = buttonStyle || themedContainerStyle;
        let buttonState = 'active';
        if (disabled) {
            buttonState = 'disabled';
        }
        if (this.state.pressed) {
            buttonState = 'pressed';
        }
        return Object.assign({}, btnStyle.containerStyle, { backgroundColor: btnStyle.buttonColors[buttonState], borderColor: btnStyle.borderColors[buttonState] });
    }
    getBorderStyle() {
        const { buttonStyle, themedContainerStyle } = this.props;
        const btnStyle = buttonStyle || themedContainerStyle;
        return Object.assign({}, btnStyle.containerStyle, { borderRadius: btnStyle.containerStyle.borderRadius === R.border.radius.default
                ? R.border.radius.default + 2
                : btnStyle.containerStyle.borderRadius });
    }
    getTextStyle() {
        const { buttonStyle, disabled, IconStart, IconEnd, size, theme, themedContainerStyle } = this.props;
        const btnStyle = buttonStyle || themedContainerStyle;
        const textStyle = Object.assign({}, getButtonTextStyle(size), btnStyle.textStyle, { marginLeft: IconStart ? R.spacing.xs : 0, marginRight: IconEnd ? R.spacing.xs : 0 });
        if (disabled) {
            textStyle.color = btnStyle.textColors ? btnStyle.textColors.disabled : theme.lightSecondary;
        }
        else if (this.state.pressed) {
            textStyle.color = btnStyle.textColors ? btnStyle.textColors.pressed : theme.lightPrimary;
        }
        else {
            textStyle.color = btnStyle.textColors ? btnStyle.textColors.active : theme.lightPrimary;
        }
        return textStyle;
    }
    render() {
        const _a = this.props, { disabled, IconEnd, IconStart, innerStyle, isFeedbackEnabled, loading, style, text, label, buttonStyle, size, variant, accessibilityLabel } = _a, props = __rest(_a, ["disabled", "IconEnd", "IconStart", "innerStyle", "isFeedbackEnabled", "loading", "style", "text", "label", "buttonStyle", "size", "variant", "accessibilityLabel"]);
        const defaultFlexValue = DRNUtil.isWeb() ? 1 : 0;
        const flattenStyle = StyleSheet.flatten(style);
        const textStyle = this.getTextStyle();
        const flattenTextStyle = StyleSheet.flatten(textStyle);
        const loadingIndicatorProps = {
            style: {
                position: 'absolute',
                zIndex: 10,
            },
            color: flattenTextStyle.color || undefined,
        };
        const isLoadingStyle = {
            opacity: loading ? 0 : undefined,
        };
        return (<View>
        
        <View style={[
            this.getBorderStyle(),
            style,
            {
                borderColor: R.opacity.translucent(R.color.uiBluePrimary),
                borderWidth: R.border.width.bold,
                top: -2,
                bottom: -2,
                left: -2,
                right: -2,
                position: 'absolute',
                opacity: this.state.focused ? 1 : 0,
            },
        ]}/>
        <DRNTouchableWrapper {...props} disabled={disabled || loading} isFeedbackEnabled={isFeedbackEnabled} onPressIn={this.handlePressIn} onPressOut={this.handlePressOut} accessibilityRole="button" accessibilityLabel={this.getAccessibilityLabel()} style={{ outlineWidth: 0 }} onFocus={this.handleFocus} onBlur={this.handleBlur}>
          
          <View pointerEvents={disabled || loading ? 'none' : 'auto'} style={[
            {
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
            },
            this.getContainerStyle(),
            style,
        ]} accessibilityRole="none">
            <Animated.View style={[
            [
                {
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flex: (flattenStyle && flattenStyle.flex) || defaultFlexValue,
                    position: 'relative',
                },
                innerStyle,
            ],
        ]} accessibilityRole="none">
              {loading && <DRNLoadingIndicator {...loadingIndicatorProps}/>}
              {IconStart && <IconStart style={isLoadingStyle}/>}
              {!!text && <DRNText style={[textStyle, isLoadingStyle]}>{text}</DRNText>}
              {IconEnd && <IconEnd style={isLoadingStyle}/>}
            </Animated.View>
          </View>
        </DRNTouchableWrapper>
      </View>);
    }
}
BaseButton.smartKeyName = 'text';
BaseButton.propTypes = Object.assign({}, TouchableWithoutFeedback.propTypes, { buttonStyle: PropTypes.shape({
        buttonColors: PropTypes.any.isRequired,
        containerStyle: PropTypes.any.isRequired,
        textColors: PropTypes.any.isRequired,
        textStyle: PropTypes.any.isRequired,
        borderColors: PropTypes.any.isRequired,
    }), disabled: PropTypes.bool, IconEnd: PropTypes.func, IconStart: PropTypes.func, innerStyle: PropTypes.oneOfType([PropTypes.number, PropTypes.object, PropTypes.arrayOf(PropTypes.object)]), isFeedbackEnabled: PropTypes.bool, loading: PropTypes.bool, size: PropTypes.oneOf(Object.values(SIZE)), text: PropTypes.string, label: PropTypes.string, variant: PropTypes.oneOf(Object.values(VARIANT)), 
    // Theme-related props. Handled by withThemedStyles HOC
    theme: PropTypes.object, themedContainerStyle: PropTypes.object });
BaseButton.defaultProps = {
    disabled: false,
    IconEnd: null,
    IconStart: null,
    isFeedbackEnabled: true,
    loading: false,
    size: SIZE.MEDIUM,
    text: null,
    label: null,
    variant: VARIANT.PRIMARY,
};
export default withSmartKey(withThemedStyles(BaseButton));
